import React from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { AppFooterColumn } from './types';
import styles from './AppFooter.module.css';

export interface AppFooterProps {
  logo?: React.ReactNode;
  tagline?: string;
  columns?: AppFooterColumn[];
  legal?: string;
  social?: React.ReactNode;
  /** Shorter in-app footer: hides tagline and tightens spacing. */
  compact?: boolean;
  /** When true, only the summary bar is shown until expanded. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  className?: string;
}

export const AppFooter: React.FC<AppFooterProps> = ({
  logo = null,
  tagline = 'A control system for business execution.',
  columns = [],
  legal = '© Krysalis',
  social = null,
  compact = false,
  collapsed = false,
  onToggleCollapsed,
  className,
}) => (
  <footer
    className={[
      styles.footer,
      compact ? styles.compact : '',
      collapsed ? styles.collapsed : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ')}
    data-testid="app-footer"
  >
    {collapsed ? (
      <div className={styles.collapsedBar}>
        <span className={styles.legal}>{legal}</span>
        {onToggleCollapsed ? (
          <button type="button" className={styles.toggleBtn} onClick={onToggleCollapsed}>
            <UpOutlined aria-hidden="true" />
            Show footer
          </button>
        ) : null}
      </div>
    ) : (
      <>
        <div className={styles.main}>
          <div className={styles.brand}>
            {logo ? <div className={styles.logoWrap}>{logo}</div> : null}
            {!compact ? <p className={styles.tagline}>{tagline}</p> : null}
          </div>
          {columns.length > 0 ? (
            <div className={styles.columns}>
              {columns.map((col) => (
                <div key={col.title}>
                  <div className={styles.columnTitle}>{col.title}</div>
                  <ul className={styles.linkList}>
                    {col.links.map((link) => (
                      <li key={link.label}>
                        <a className={styles.link} href={link.href}>
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className={styles.bottomBar}>
          <div className={styles.bottomInner}>
            <span className={styles.legal}>{legal}</span>
            <div className={styles.bottomActions}>
              {social ? <div className={styles.social}>{social}</div> : null}
              {onToggleCollapsed ? (
                <button type="button" className={styles.toggleBtn} onClick={onToggleCollapsed}>
                  <DownOutlined aria-hidden="true" />
                  Hide footer
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </>
    )}
  </footer>
);
